import { useState, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronsUpDown, Check, Search } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { apiClient } from "@/lib/api"
import { api } from "@/lib/api"
import { workflowApi } from "@/services/api/workflow.api"
import { ClientAvatar } from "@/components/client-avatar"
import { ContextSwitchOverlay } from "@/components/context-switch-overlay"
import { beginContextSwitch, switchHoldMs, type PendingContextSwitch } from "@/lib/context-switch"
import { cn } from "@/lib/utils"

const ACTIVE_LICENSE_KEY = "activeLicenseId"

export function SidebarClientSelector() {
  const { licenses } = useAuth()
  const location = useLocation()
  const [activeLicenseId, setActiveLicenseId] = useState<number | null>(() => {
    const stored = localStorage.getItem(ACTIVE_LICENSE_KEY)
    return stored ? parseInt(stored) : null
  })

  // Set first license as active if none selected, or if the stored selection
  // no longer exists (e.g. that client's license was deactivated and the
  // auth payload stopped returning it)
  useEffect(() => {
    const selectionValid = activeLicenseId && licenses.some((l) => l.id === activeLicenseId)
    if (licenses.length > 0 && !selectionValid) {
      const firstLicense = licenses[0]
      setActiveLicenseId(firstLicense.id)
      localStorage.setItem(ACTIVE_LICENSE_KEY, firstLicense.id.toString())
      apiClient.setActiveLicense(firstLicense.id, firstLicense.domain)
      workflowApi.setActiveLicense(firstLicense.id, firstLicense.domain)
    }
  }, [licenses, activeLicenseId])

  // Update API headers when active license changes
  useEffect(() => {
    const activeLicense = licenses.find((l) => l.id === activeLicenseId)
    if (activeLicense) {
      apiClient.setActiveLicense(activeLicense.id, activeLicense.domain)
      workflowApi.setActiveLicense(activeLicense.id, activeLicense.domain)
    }
  }, [activeLicenseId, licenses])

  const [searchQuery, setSearchQuery] = useState("")
  // Destination of an in-flight switch — drives the "leaving" overlay. Held
  // here, above the dropdown, so closing the menu can't unmount mid-switch.
  const [pendingSwitch, setPendingSwitch] = useState<PendingContextSwitch | null>(null)
  const activeLicense = licenses.find((l) => l.id === activeLicenseId)

  const filteredLicenses = useMemo(() => {
    if (!searchQuery.trim()) return licenses
    const q = searchQuery.toLowerCase()
    return licenses.filter(l =>
      l.client_name.toLowerCase().includes(q) ||
      l.domain.toLowerCase().includes(q)
    )
  }, [licenses, searchQuery])

  // Fetch domain rating + logo from backend (DR cached daily, logo from profile)
  const { data: drData } = useQuery({
    queryKey: ['domain-rating', activeLicenseId],
    queryFn: () => api.settings.getDomainRating(),
    enabled: !!activeLicense,
    staleTime: 1000 * 60 * 60,
    retry: false,
  })

  const domainRating = drData?.domain_rating
  const drChange = drData?.change
  const logoUrl = drData?.logo_url

  // Switching is a reload, so the "leaving" card has to paint before we hand
  // the page over — hence state-then-timer rather than switching in the click
  // handler, which would let the reload preempt the first frame.
  useEffect(() => {
    if (!pendingSwitch) return
    const timer = setTimeout(() => {
      // Tasks aren't site-scoped, so a switch from there lands on the dashboard.
      const isOnTasks = location.pathname === "/tasks" || location.pathname.startsWith("/tasks/")
      beginContextSwitch(pendingSwitch, isOnTasks ? "/" : undefined)
    }, switchHoldMs())
    return () => clearTimeout(timer)
  }, [pendingSwitch, location.pathname])

  // Headers and localStorage are left alone until beginContextSwitch runs:
  // re-pointing them mid-animation would only fire requests the reload discards.
  const handleLicenseChange = (license: { id: number; client_name: string; domain: string }) => {
    setPendingSwitch({
      licenseId: license.id,
      clientName: license.client_name,
      domain: license.domain,
    })
  }

  // No licenses — parent handles the header for this case
  if (!activeLicense || licenses.length === 0) {
    return null
  }

  // Format DR change as "+2" or "-1"
  const changeText = drChange !== null && drChange !== undefined
    ? `${drChange > 0 ? '+' : ''}${drChange}`
    : null

  return (
    <>
      {/* Portalled to the body: the sidebar is a transformed, clipped ancestor,
          and the dropdown unmounts its content on close. Neither can be allowed
          to swallow a full-screen overlay. The target's uploaded logo isn't
          knowable before the switch, so this falls back to favicon/initials. */}
      {pendingSwitch && createPortal(
        <ContextSwitchOverlay
          clientName={pendingSwitch.clientName}
          domain={pendingSwitch.domain}
          phase="leaving"
        />,
        document.body
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* In the icon rail only the logo shows, shrunk to the rail's 32px icon slot. */}
          <button className="flex w-full items-center gap-3 rounded-lg px-1 py-1 hover:bg-muted transition-colors text-left group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0">
            <span className="shrink-0 group-data-[collapsible=icon]:[&>*]:h-8 group-data-[collapsible=icon]:[&>*]:w-8 group-data-[collapsible=icon]:[&>*]:text-sm">
              <ClientAvatar
                name={activeLicense.client_name}
                domain={activeLicense.domain}
                website={activeLicense.domain}
                logoUrl={logoUrl}
                size="md"
              />
            </span>
            <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
              {domainRating !== null && domainRating !== undefined ? (
                <div className="text-[11px] text-muted-foreground leading-tight">
                  DR {domainRating}
                  {changeText && (
                    <span className={drChange! > 0 ? 'text-emerald-400 ml-1' : drChange! < 0 ? 'text-red-400 ml-1' : 'ml-1'}>
                      {changeText} MoM
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Forge</div>
              )}
              <div className="font-medium truncate">{activeLicense.client_name}</div>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0 group-data-[collapsible=icon]:hidden" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[240px] md:w-[460px] p-0">
          {/* Search filter */}
          {licenses.length > 6 && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                  autoFocus
                />
              </div>
            </div>
          )}
          {/* Site grid — 2 columns on desktop, 1 on mobile */}
          <div className={cn(
            "grid gap-0.5 p-1.5 max-h-[400px] overflow-y-auto",
            filteredLicenses.length > 1 ? "md:grid-cols-2" : "grid-cols-1"
          )}>
            {filteredLicenses.map((license) => (
              <button
                key={license.id}
                onClick={() => { handleLicenseChange(license); setSearchQuery("") }}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm transition-colors w-full",
                  license.id === activeLicenseId
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <ClientAvatar
                  name={license.client_name}
                  domain={license.domain}
                  website={license.domain}
                  logoUrl={license.id === activeLicenseId ? logoUrl : undefined}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-xs">{license.client_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{license.domain} <span className="opacity-50">({license.site_id ?? license.id})</span></div>
                </div>
                {license.id === activeLicenseId && (
                  <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                )}
              </button>
            ))}
            {filteredLicenses.length === 0 && (
              <div className="col-span-full text-center py-4 text-sm text-muted-foreground">
                No sites match your search
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
